const trimSlash = (value) => String(value || "").replace(/\/+$/, "");
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);
const RETRYABLE_STATUS_CODES = new Set([404, 502, 503, 504]);

function isAbsoluteUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function normalizeApiPath(path) {
  if (!path) return "";
  if (isAbsoluteUrl(path)) return String(path);
  return String(path).startsWith("/") ? String(path) : `/${path}`;
}

function isRetryableFetchError(error) {
  if (!error) return false;
  if (error instanceof TypeError) return true;
  return /fetch|network|load failed/i.test(String(error.message || ""));
}

export function getApiBaseCandidates() {
  const configured = trimSlash(process.env.REACT_APP_BACKEND_URL || "");
  const candidates = [];

  if (configured) {
    candidates.push(`${configured}/api`);
  }

  if (typeof window !== "undefined") {
    const { hostname, origin } = window.location;
    if (LOCAL_HOSTS.has(hostname)) {
      candidates.push("http://127.0.0.1:8001/api", "http://localhost:8001/api");
    } else if (hostname) {
      const rootHost = hostname.startsWith("www.") ? hostname.slice(4) : hostname;
      candidates.push(`${trimSlash(origin)}/api`, `https://api.${rootHost}/api`);
    }
  }

  if (candidates.length === 0) {
    candidates.push("http://localhost:8001/api");
  }

  return [...new Set(candidates)];
}

export function getApiBase() {
  return getApiBaseCandidates()[0];
}

export function resolveApiUrl(path, base = getApiBase()) {
  const normalizedPath = normalizeApiPath(path);
  if (!normalizedPath) return base;
  if (isAbsoluteUrl(normalizedPath)) return normalizedPath;
  return `${trimSlash(base)}${normalizedPath}`;
}

export async function fetchApi(path, options = {}) {
  const normalizedPath = normalizeApiPath(path);
  const urls = isAbsoluteUrl(normalizedPath)
    ? [normalizedPath]
    : getApiBaseCandidates().map((base) => resolveApiUrl(normalizedPath, base));

  let lastError = null;
  let lastResponse = null;

  for (let index = 0; index < urls.length; index += 1) {
    const url = urls[index];
    try {
      const response = await fetch(url, options);
      if (!RETRYABLE_STATUS_CODES.has(response.status) || index === urls.length - 1) {
        return response;
      }
      lastResponse = response;
    } catch (error) {
      lastError = error;
      if (!isRetryableFetchError(error) || index === urls.length - 1) {
        throw error;
      }
    }
  }

  if (lastResponse) {
    return lastResponse;
  }

  throw lastError || new Error("Request failed");
}
